import { css } from "goober";
import { useState } from "react";
import { useWorkflowChangeReview } from "../../hooks/useWorkflowChangeReview";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  createPullRequestUrl,
  pullRequestDescription,
} from "./pullRequestDescription";

const badgeClass = css`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 38px;
  background: var(--alpha4);
  color: var(--text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  &:hover {
    background: var(--alpha8);
  }
`;

export function CreatePullRequest({
  remoteUrl,
  gitBranchName,
  workflowId,
  commitId,
  canGenerate,
}: {
  remoteUrl: string;
  gitBranchName: string;
  workflowId: string;
  commitId: string | null;
  canGenerate: boolean;
}) {
  const {
    review,
    generating,
    canGenerate: ready,
    generate,
  } = useWorkflowChangeReview({ workflowId, commitId });
  const [attempted, setAttempted] = useState(false);
  const url = createPullRequestUrl(
    remoteUrl,
    gitBranchName,
    review ? pullRequestDescription(review) : ""
  );
  if (!url) return null;
  const label = url.isGitlab ? "Create MR" : "Create PR";
  const provider = url.isGitlab ? "GitLab" : "GitHub";
  const needsReview =
    canGenerate &&
    commitId !== null &&
    review?.status !== "complete" &&
    !attempted;
  const content = (
    <>
      <Icon name="git-branch" size={12} />
      <Text variant="text-small-md">
        {generating ? "Generating description…" : label}
      </Text>
    </>
  );
  return (
    <Tooltip
      contentStyle={{ maxWidth: 200 }}
      content={
        attempted && review?.status !== "complete" && !generating
          ? `Description generation failed. Continue on ${provider} to write your own.`
          : `${label} on ${provider}`
      }
    >
      {needsReview || generating ? (
        <ClickButton
          className={badgeClass}
          disabled={!ready}
          aria-busy={generating}
          onClick={async () => {
            await generate();
            setAttempted(true);
          }}
        >
          {content}
        </ClickButton>
      ) : (
        <Link
          className={badgeClass}
          to={url.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {content}
        </Link>
      )}
    </Tooltip>
  );
}
